import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, AlertTriangle, Download, Film, Upload } from "lucide-react";

import Seo from "@/components/Seo";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import {
  TEMPLATE_GROUPS,
  CONTENT_TYPES,
  findTemplate,
  type ContentType,
} from "@/lib/graphicTemplates";
import {
  renderPng,
  renderMp4,
  downloadBlob,
  RENDER_BASE_URL,
  type MatchGraphic,
} from "@/lib/render";

type ClubRow = {
  id: string;
  club_name: string;
  short_name: string | null;
  primary_color: string | null;
  secondary_color: string | null;
  paper_color: string | null;
  logo_url: string | null;
  liga: string | null;
};

const emptyLineup = () => Array.from({ length: 11 }, () => "");

export default function GrafikenPage() {
  const { user, loading: authLoading } = useAuth();
  const { toast } = useToast();

  const [clubs, setClubs] = useState<ClubRow[]>([]);
  const [clubId, setClubId] = useState<string>("");
  const [template, setTemplate] = useState<string>("D9-S");
  const [contentType, setContentType] = useState<ContentType>("spieltag");

  const [theme, setTheme] = useState({ primary: "#1B5EAB", accent: "#00D26A", paper: "#FFFFFF" });
  const [home, setHome] = useState({ name: "", short: "", crestUrl: "" });
  const [away, setAway] = useState({ name: "", short: "", crestUrl: "" });
  const [competition, setCompetition] = useState("");
  const [venue, setVenue] = useState("");
  const [kickoff, setKickoff] = useState("");
  const [scoreHome, setScoreHome] = useState("0");
  const [scoreAway, setScoreAway] = useState("0");
  const [scorerName, setScorerName] = useState("");
  const [scorerMinute, setScorerMinute] = useState("");
  const [lineup, setLineup] = useState<string[]>(emptyLineup);
  const [subs, setSubs] = useState("");
  const [playerPhotoUrl, setPlayerPhotoUrl] = useState("");

  const [previewUrl, setPreviewUrl] = useState<string>("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string>("");
  const [downloading, setDownloading] = useState<"png" | "mp4" | null>(null);
  const [uploading, setUploading] = useState<string>("");

  const abortRef = useRef<AbortController | null>(null);
  const previewUrlRef = useRef<string>("");

  const tpl = findTemplate(template);
  const isStory = tpl?.format === "story";
  const needsPhoto = !!tpl?.needsPlayerPhoto;
  const effectiveContentType: ContentType = tpl?.isResult ? "ergebnis" : contentType;

  /* ---------------- Vereine laden ---------------- */
  useEffect(() => {
    if (!user) return;
    supabase
      .from("clubs")
      .select("id, club_name, short_name, primary_color, secondary_color, paper_color, logo_url, liga")
      .order("created_at", { ascending: false })
      .then(({ data, error }) => {
        if (error) return;
        const rows = (data ?? []) as ClubRow[];
        setClubs(rows);
        if (rows[0]) setClubId(rows[0].id);
      });
  }, [user]);

  useEffect(() => {
    const club = clubs.find((c) => c.id === clubId);
    if (!club) return;
    setTheme({
      primary: club.primary_color || "#1B5EAB",
      accent: club.secondary_color || "#00D26A",
      paper: club.paper_color || "#FFFFFF",
    });
    setHome({
      name: club.club_name,
      short: club.short_name || club.club_name.slice(0, 3).toUpperCase(),
      crestUrl: club.logo_url || "",
    });
    if (club.liga) setCompetition((prev) => prev || club.liga!);
  }, [clubId, clubs]);

  /* ---------------- Payload ---------------- */
  const payload = useMemo<MatchGraphic>(() => {
    const data: MatchGraphic = {
      template,
      theme,
      home,
      away,
      competition,
    };
    if (effectiveContentType === "spieltag") {
      if (venue) data.venue = venue;
      if (kickoff) data.kickoff = new Date(kickoff).toISOString();
    }
    if (effectiveContentType === "tor" || effectiveContentType === "ergebnis") {
      data.score = [Number(scoreHome) || 0, Number(scoreAway) || 0];
    }
    if (effectiveContentType === "tor" && scorerName) {
      data.scorer = { name: scorerName, minute: Number(scorerMinute) || 0 };
    }
    if (effectiveContentType === "aufstellung") {
      data.lineup = lineup.map((n) => n.trim()).filter(Boolean);
      const s = subs.split(/[\n,]/).map((n) => n.trim()).filter(Boolean);
      if (s.length) data.subs = s;
    }
    if (needsPhoto && playerPhotoUrl) data.playerPhotoUrl = playerPhotoUrl;
    return data;
  }, [
    template, theme, home, away, competition, effectiveContentType, venue, kickoff,
    scoreHome, scoreAway, scorerName, scorerMinute, lineup, subs, needsPhoto, playerPhotoUrl,
  ]);

  const blocked = needsPhoto && !playerPhotoUrl;

  /* ---------------- Live-Vorschau (debounced) ---------------- */
  useEffect(() => {
    if (blocked) {
      setPreviewLoading(false);
      return;
    }
    const timer = setTimeout(async () => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      setPreviewLoading(true);
      setPreviewError("");
      try {
        const blob = await renderPng(payload, controller.signal);
        if (controller.signal.aborted) return;
        const url = URL.createObjectURL(blob);
        if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
        previewUrlRef.current = url;
        setPreviewUrl(url);
      } catch (err) {
        if ((err as Error).name === "AbortError") return;
        setPreviewError(
          (err as Error).message || "Die Vorschau konnte nicht geladen werden.",
        );
      } finally {
        if (!controller.signal.aborted) setPreviewLoading(false);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [payload, blocked]);

  useEffect(() => {
    return () => {
      abortRef.current?.abort();
      if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
    };
  }, []);

  /* ---------------- Uploads ---------------- */
  const uploadFile = useCallback(
    async (file: File, slot: string): Promise<string | null> => {
      if (!user) return null;
      setUploading(slot);
      try {
        const ext = file.name.split(".").pop() || "png";
        const path = `${user.id}/${slot}-${Date.now()}.${ext}`;
        const { error } = await supabase.storage.from("grafiken").upload(path, file, {
          upsert: true,
          contentType: file.type,
        });
        if (error) throw error;
        const { data, error: signErr } = await supabase.storage
          .from("grafiken")
          .createSignedUrl(path, 60 * 60 * 24 * 7);
        if (signErr || !data) throw signErr ?? new Error("Signierte URL fehlgeschlagen");
        return data.signedUrl;
      } catch (err) {
        toast({
          title: "Upload fehlgeschlagen",
          description: (err as Error).message,
          variant: "destructive",
        });
        return null;
      } finally {
        setUploading("");
      }
    },
    [user, toast],
  );

  /* ---------------- Downloads ---------------- */
  const handleDownload = async (kind: "png" | "mp4") => {
    setDownloading(kind);
    try {
      const blob = kind === "png" ? await renderPng(payload) : await renderMp4(payload);
      downloadBlob(blob, `${template}-${Date.now()}.${kind}`);
    } catch (err) {
      toast({
        title: "Download fehlgeschlagen",
        description: (err as Error).message,
        variant: "destructive",
      });
    } finally {
      setDownloading(null);
    }
  };

  /* ---------------- Auth-Gate ---------------- */
  if (authLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="mx-auto flex min-h-[60vh] max-w-md flex-col items-center justify-center gap-4 px-4 text-center">
        <h1 className="text-2xl font-bold">Grafik-Studio</h1>
        <p className="text-muted-foreground">
          Bitte melde dich an, um Matchday-Grafiken für deinen Verein zu erstellen.
        </p>
        <Button asChild>
          <Link to="/login">Zum Login</Link>
        </Button>
      </div>
    );
  }

  const accentStyle = { backgroundColor: theme.primary, color: "#fff" } as const;

  return (
    <div className="mx-auto w-full max-w-6xl px-4 pb-24 pt-24">
      <Seo
        title="Grafik-Studio – Matchday-Grafiken erstellen | Torflash"
        description="Erstelle aus deinen Spieldaten fertige Matchday-Grafiken als PNG oder MP4-Story für deinen Amateurfußball-Verein."
      />

      <header className="mb-6">
        <h1 className="text-3xl font-bold tracking-tight">Grafik-Studio</h1>
        <p className="mt-1 text-muted-foreground">
          Spieldaten eingeben, Vorlage wählen, Grafik herunterladen.
        </p>
      </header>

      {!RENDER_BASE_URL && (
        <div className="mb-6 flex items-start gap-3 rounded-lg border border-destructive/40 bg-destructive/10 p-4 text-sm">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
          <span>
            Der Grafik-Dienst ist noch nicht hinterlegt. Trage die Adresse des Render-Dienstes als
            <code className="mx-1 rounded bg-muted px-1">VITE_RENDER_BASE_URL</code>
            ein, dann funktionieren Vorschau und Download.
          </span>
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-[1fr_380px]">
        {/* ---------------- Eingaben ---------------- */}
        <div className="space-y-6">
          <Card className="space-y-4 p-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>Verein</Label>
                <Select value={clubId} onValueChange={setClubId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Verein wählen" />
                  </SelectTrigger>
                  <SelectContent>
                    {clubs.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.club_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Inhalt</Label>
                <Select
                  value={effectiveContentType}
                  onValueChange={(v) => setContentType(v as ContentType)}
                  disabled={!!tpl?.isResult}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CONTENT_TYPES.map((c) => (
                      <SelectItem key={c.value} value={c.value}>
                        {c.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-3">
              {(["primary", "accent", "paper"] as const).map((key) => (
                <div key={key} className="space-y-2">
                  <Label className="capitalize">
                    {key === "primary" ? "Hauptfarbe" : key === "accent" ? "Akzentfarbe" : "Hintergrund"}
                  </Label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={theme[key]}
                      onChange={(e) => setTheme((t) => ({ ...t, [key]: e.target.value }))}
                      className="h-9 w-10 cursor-pointer rounded border border-border bg-transparent"
                      aria-label={key}
                    />
                    <Input
                      value={theme[key]}
                      onChange={(e) => setTheme((t) => ({ ...t, [key]: e.target.value }))}
                    />
                  </div>
                </div>
              ))}
            </div>
          </Card>

          {/* Vorlagen-Picker */}
          <Card className="p-4">
            <h2 className="mb-3 text-lg font-semibold">Vorlage</h2>
            <div className="max-h-[420px] space-y-5 overflow-y-auto pr-1">
              {TEMPLATE_GROUPS.map((group) => (
                <div key={group.label}>
                  <p className="mb-2 text-sm font-medium text-muted-foreground">{group.label}</p>
                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4">
                    {group.templates.map((t) => {
                      const active = t.id === template;
                      return (
                        <button
                          key={t.id}
                          type="button"
                          onClick={() => setTemplate(t.id)}
                          style={active ? { borderColor: theme.primary } : undefined}
                          className={`rounded-lg border p-2 text-left transition ${
                            active ? "border-2 bg-muted/60" : "border-border hover:bg-muted/40"
                          }`}
                        >
                          <span className="block text-sm font-semibold">{t.id}</span>
                          <span className="mt-1 flex flex-wrap gap-1">
                            <Badge variant="secondary" className="text-[10px]">
                              {t.format === "story" ? "Story" : "Post"}
                            </Badge>
                            {t.needsPlayerPhoto && (
                              <Badge variant="outline" className="text-[10px]">
                                Spielerfoto
                              </Badge>
                            )}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </Card>

          {/* Formular */}
          <Card className="space-y-4 p-4">
            <h2 className="text-lg font-semibold">Spieldaten</h2>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>Heim – Name</Label>
                <Input value={home.name} onChange={(e) => setHome({ ...home, name: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Heim – Kürzel</Label>
                <Input value={home.short} onChange={(e) => setHome({ ...home, short: e.target.value })} />
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>Gegner – Name</Label>
                <Input value={away.name} onChange={(e) => setAway({ ...away, name: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Gegner – Kürzel</Label>
                <Input value={away.short} onChange={(e) => setAway({ ...away, short: e.target.value })} />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Gegner – Wappen</Label>
              <div className="flex items-center gap-3">
                <Input
                  placeholder="https://…"
                  value={away.crestUrl}
                  onChange={(e) => setAway({ ...away, crestUrl: e.target.value })}
                />
                <label className="inline-flex shrink-0 cursor-pointer items-center gap-2 rounded-md border border-border px-3 py-2 text-sm">
                  {uploading === "crest" ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Upload className="h-4 w-4" />
                  )}
                  Upload
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      const url = await uploadFile(file, "crest");
                      if (url) setAway((a) => ({ ...a, crestUrl: url }));
                    }}
                  />
                </label>
                {away.crestUrl && (
                  <img
                    src={away.crestUrl}
                    alt="Wappen des Gegners"
                    className="h-10 w-10 shrink-0 rounded object-contain"
                  />
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Wettbewerb</Label>
              <Input
                placeholder="z. B. B-Klasse Gruppe 4"
                value={competition}
                onChange={(e) => setCompetition(e.target.value)}
              />
            </div>

            {effectiveContentType === "spieltag" && (
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label>Spielort</Label>
                  <Input value={venue} onChange={(e) => setVenue(e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label>Anstoß</Label>
                  <Input
                    type="datetime-local"
                    value={kickoff}
                    onChange={(e) => setKickoff(e.target.value)}
                  />
                </div>
              </div>
            )}

            {(effectiveContentType === "tor" || effectiveContentType === "ergebnis") && (
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label>Tore Heim</Label>
                  <Input
                    type="number"
                    min={0}
                    value={scoreHome}
                    onChange={(e) => setScoreHome(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Tore Gegner</Label>
                  <Input
                    type="number"
                    min={0}
                    value={scoreAway}
                    onChange={(e) => setScoreAway(e.target.value)}
                  />
                </div>
              </div>
            )}

            {effectiveContentType === "tor" && (
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label>Torschütze</Label>
                  <Input value={scorerName} onChange={(e) => setScorerName(e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label>Minute</Label>
                  <Input
                    type="number"
                    min={0}
                    value={scorerMinute}
                    onChange={(e) => setScorerMinute(e.target.value)}
                  />
                </div>
              </div>
            )}

            {effectiveContentType === "aufstellung" && (
              <div className="space-y-4">
                <div>
                  <Label>Startelf</Label>
                  <div className="mt-2 grid gap-2 sm:grid-cols-2">
                    {lineup.map((name, i) => (
                      <Input
                        key={i}
                        placeholder={`Spieler ${i + 1}`}
                        value={name}
                        onChange={(e) => {
                          const next = [...lineup];
                          next[i] = e.target.value;
                          setLineup(next);
                        }}
                      />
                    ))}
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Auswechselspieler (mit Komma oder Zeilenumbruch trennen)</Label>
                  <Input value={subs} onChange={(e) => setSubs(e.target.value)} />
                </div>
              </div>
            )}

            {needsPhoto && (
              <>
                <Separator />
                <div className="space-y-2">
                  <Label>Freigestelltes Spielerfoto (PNG mit Transparenz)</Label>
                  <div className="flex items-center gap-3">
                    <Input
                      placeholder="https://…"
                      value={playerPhotoUrl}
                      onChange={(e) => setPlayerPhotoUrl(e.target.value)}
                    />
                    <label className="inline-flex shrink-0 cursor-pointer items-center gap-2 rounded-md border border-border px-3 py-2 text-sm">
                      {uploading === "player" ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Upload className="h-4 w-4" />
                      )}
                      Upload
                      <input
                        type="file"
                        accept="image/png"
                        className="hidden"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          const url = await uploadFile(file, "player");
                          if (url) setPlayerPhotoUrl(url);
                        }}
                      />
                    </label>
                    {playerPhotoUrl && (
                      <img
                        src={playerPhotoUrl}
                        alt="Spielerfoto"
                        className="h-12 w-12 shrink-0 rounded object-contain"
                      />
                    )}
                  </div>
                </div>
              </>
            )}
          </Card>
        </div>

        {/* ---------------- Vorschau ---------------- */}
        <div className="lg:sticky lg:top-24 lg:self-start">
          <Card className="space-y-4 p-4">
            <h2 className="text-lg font-semibold">Vorschau</h2>
            <div
              className={`relative flex w-full items-center justify-center overflow-hidden rounded-lg bg-muted ${
                isStory ? "aspect-[9/16]" : "aspect-[4/5]"
              }`}
            >
              {blocked ? (
                <p className="px-6 text-center text-sm text-muted-foreground">
                  Diese Vorlage braucht ein freigestelltes Spielerfoto. Lade eins hoch, dann startet
                  die Vorschau.
                </p>
              ) : previewError ? (
                <p className="px-6 text-center text-sm text-destructive">{previewError}</p>
              ) : previewUrl ? (
                <img
                  src={previewUrl}
                  alt={`Vorschau der Vorlage ${template}`}
                  className="h-full w-full object-contain"
                />
              ) : (
                <p className="text-sm text-muted-foreground">Noch keine Vorschau</p>
              )}

              {previewLoading && (
                <div className="absolute inset-0 flex items-center justify-center bg-background/60">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Button
                className="w-full"
                style={accentStyle}
                disabled={blocked || downloading !== null}
                onClick={() => handleDownload("png")}
              >
                {downloading === "png" ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Download className="mr-2 h-4 w-4" />
                )}
                PNG herunterladen
              </Button>
              <Button
                variant="outline"
                className="w-full"
                disabled={!isStory || blocked || downloading !== null}
                onClick={() => handleDownload("mp4")}
              >
                {downloading === "mp4" ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Film className="mr-2 h-4 w-4" />
                )}
                MP4-Story herunterladen
              </Button>
              {!isStory && (
                <p className="text-center text-xs text-muted-foreground">
                  MP4 gibt es nur für Story-Vorlagen.
                </p>
              )}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
